<script lang="ts">
  import { emptyMeltElement, melt } from "@melt-ui/svelte";
  import type { Snippet } from "svelte";
  import type { HTMLAnchorAttributes, HTMLButtonAttributes } from "svelte/elements";
  import type { Except } from "type-fest";

  export type ButtonProps = {
    variant?: "primary" | "primaryV2" | "secondary" | "tertiary" | "unstyled" | "error";
    shape?: "rounded" | "square" | "circle" | "default";
    size?: "xxs" | "xs" | "sm" | "md";
    disabled?: boolean;
    meltElement?: any;
    morePadding?: boolean;
    prefix?: Snippet;
    suffix?: Snippet;
  } & (
    | ({ as?: "button" } & Except<HTMLButtonAttributes, "prefix">)
    | ({ as?: "a" } & Except<HTMLAnchorAttributes, "prefix">)
  );

  let {
    variant = "primary",
    shape = "default",
    size = "md",
    disabled = false,
    as = "button",
    meltElement = emptyMeltElement,
    morePadding = false,
    prefix,
    suffix,
    children,
    class: className,
    ...rest
  }: ButtonProps = $props();

  const VARIANTS = {
    primary: "bg-mono-900 text-mono-000 hover:bg-mono-800 active:bg-mono-700",
    primaryV2: "bg-pri-800 text-mono-100 hover:bg-pri-700 active:bg-pri-700",
    secondary: "bg-mono-000 text-mono-900 shadow-border-inset hover:bg-mono-200 active:bg-mono-300",
    tertiary: "text-foreground-secondary bg-transparent hover:bg-alpha-100 active:bg-alpha-200",
    error: "bg-sec-900 text-mono-100 dark:text-mono-900 hover:bg-sec-800 active:bg-sec-800",
    unstyled: "",
  } as const;
</script>

<svelte:element
  this={as}
  {...$meltElement} use:$meltElement.action
  class:disabled
  {...rest}
  {disabled}
  class={[
    variant === "unstyled"
      ? "focus-ring"
      : "button flex max-w-full shrink-0 cursor-pointer items-center justify-center px-3 text-sm focus-ring transition-colors duration-150 select-none active:scale-[0.98]",
    className,
    variant,
    shape,
    size,
    VARIANTS[variant],
    prefix ? "pl-2.5" : "",
    suffix ? "pr-2.5" : "",
    morePadding ? "!px-6" : "",
  ]}
>
  <div
    class={[
      "flex items-center gap-2",
      shape !== "circle" && shape !== "square" && variant !== "unstyled" ? "truncate" : "",
    ]}
  >
    {#if prefix}
      <span aria-hidden="true">
        {@render prefix?.()}
      </span>
    {/if}
    {@render children?.()}
    {#if suffix}
      <span aria-hidden="true">
        {@render suffix?.()}
      </span>
    {/if}
  </div>
</svelte:element>

<style>
  @reference "#tailwind.css";
  .button {
    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.5;
    }

    &.rounded {
      border-radius: 9999px;
    }

    &.square,
    &.circle {
      justify-content: center;

      &.xxs {
        width: 20px;
      }

      &.xs {
        width: var(--spacing-input-height-xs);
      }

      &.sm {
        width: var(--spacing-input-height-sm);
      }

      &.md {
        width: var(--spacing-input-height-md);
      }
    }

    &:not(.unstyled) {
      &.xxs {
        height: 20px;
        border-radius: 6px;
        padding-inline: 8px;
        font-size: 12px;
      }

      &.xs {
        height: var(--spacing-input-height-xs);
        border-radius: 8px;
      }

      &.sm {
        height: var(--spacing-input-height-sm);
        border-radius: 8px;
      }

      &.md {
        height: var(--spacing-input-height-md);
        border-radius: 10px;
      }

      &:not(.rounded):not(.circle):not(.xxs):not(.xs):not(.sm):not(.md) {
        border-radius: 8px;
      }

      &.circle {
        border-radius: 50%;
      }
    }
  }
</style>
